import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A telecom radio tower: a tapered steel lattice mast with X-bracing, a ring of
// panel antennas and a microwave dish near the top, topped by a red aircraft
// warning beacon that blinks. The beacon (named "beacon") pulses in the
// animation.
const STEEL = "#878e96";
const STEEL_DK = "#6c727a";
const ANTENNA = "#aab0b6";
const DISH = "#cfd2d4";
const BEACON = "#e23b2f";

const TOP_Y = 2.2;

function strut(a: Vec3, b: Vec3, thick: number, color: string): Part {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz) || 1e-4;
  const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
  const yaw = Math.atan2(dx, dz);
  return { geometry: new THREE.BoxGeometry(thick, len, thick), color, position: mid, rotation: [pitch, yaw, 0], eulerOrder: "YXZ" };
}

export const radioTower = defineModel({
  id: "radio-tower",
  batchId: "electric",
  name: "Radio tower",
  tags: ["building", "electric", "power", "telecom", "antenna", "animated"],
  summary: "A lattice telecom mast with antennas and a blinking beacon.",
  footprint: 0.5,
  parts(): Part[] {
    const baseY = 0.12, bc = 0.22, tc = 0.1;
    const corners = (h: number, y: number): Vec3[] => [[-h, y, -h], [h, y, -h], [h, y, h], [-h, y, h]];
    const base = corners(bc, baseY), top = corners(tc, TOP_Y);
    const parts: Part[] = [
      { geometry: new THREE.BoxGeometry(0.6, 0.12, 0.6), color: "#5a5a5e", position: [0, 0.06, 0] },
    ];
    // Four legs.
    for (let i = 0; i < 4; i++) parts.push(strut(base[i], top[i], 0.04, STEEL));
    // Belts and X-bracing up the four faces.
    const levels = [baseY, 0.65, 1.15, 1.65, TOP_Y];
    for (const y of levels) {
      const h = bc + (tc - bc) * ((y - baseY) / (TOP_Y - baseY));
      const c = corners(h, y);
      for (let i = 0; i < 4; i++) parts.push(strut(c[i], c[(i + 1) % 4], 0.025, STEEL_DK));
    }
    for (let l = 0; l < levels.length - 1; l++) {
      const yA = levels[l], yB = levels[l + 1];
      const hA = bc + (tc - bc) * ((yA - baseY) / (TOP_Y - baseY));
      const hB = bc + (tc - bc) * ((yB - baseY) / (TOP_Y - baseY));
      for (const z of [-1, 1]) {
        parts.push(strut([-hA, yA, z * hA], [hB, yB, z * hB], 0.02, STEEL_DK));
      }
    }
    // A ring of three panel antennas near the top.
    for (let i = 0; i < 3; i++) {
      const a = i * (Math.PI * 2 / 3);
      const r = 0.26;
      parts.push({ geometry: new THREE.BoxGeometry(0.08, 0.34, 0.16), color: ANTENNA, position: [Math.cos(a) * r, 1.95, Math.sin(a) * r], rotation: [0, -a, 0] });
    }
    // Microwave dish on a short bracket.
    parts.push(
      { geometry: new THREE.CylinderGeometry(0.001, 0.16, 0.1, 14), color: DISH, position: [0.32, 1.5, 0.0], rotation: [0, 0, 1.4] },
      { geometry: new THREE.BoxGeometry(0.12, 0.03, 0.03), color: STEEL_DK, position: [0.2, 1.5, 0] },
    );
    // Mast tip and the red warning beacon.
    parts.push({ geometry: new THREE.CylinderGeometry(0.015, 0.025, 0.24, 6), color: STEEL, position: [0, TOP_Y + 0.12, 0] });
    parts.push({ geometry: new THREE.SphereGeometry(0.055, 8, 6), color: BEACON, position: [0, TOP_Y + 0.26, 0], name: "beacon" });
    return parts;
  },
  animations: [
    {
      id: "blink",
      label: "Beacon blinking",
      duration: 1.4,
      loop: true,
      tracks: [
        { target: "beacon", pivot: [0, TOP_Y + 0.26, 0], scale: [
          { t: 0, value: 0.7 }, { t: 0.5, value: 1.7, ease: "sine" }, { t: 1.0, value: 0.7, ease: "sine" }, { t: 1.4, value: 0.7 },
        ] },
      ],
    },
  ],
});
